import { useEffect, useMemo, useRef, useState } from 'react';

import { Box, Col, Row } from '@opendoor/atoms/box';
import { Button } from '@opendoor/atoms/button';
import { Text } from '@opendoor/atoms/text';
import { cn } from '@opendoor/atoms/utils';
import {
  IconChevronLeft,
  IconChevronRight,
  IconHeart,
  IconHeartFilled,
  IconX,
} from '@tabler/icons-react';
import { formatStreetAddress } from 'components/marketplace/helpers/addressHelpers';
import { getDisplayPrice } from 'components/marketplace/helpers/displayPrice';
import { buildGalleryPhotos } from 'components/marketplace/helpers/galleryPhotos';
import type { GalleryPhoto } from 'components/marketplace/helpers/galleryPhotos';
import {
  getLightboxThumbnailUrl,
  LIGHTBOX_BREAKPOINT_TIERS,
} from 'components/marketplace/helpers/presizedPhotos';
import type { PropertyDetailsType } from 'components/marketplace/property/types';
import { Carousel } from 'components/marketplace/shared/Carousel';
import { GalleryListingPhoto } from 'components/marketplace/shared/GalleryListingPhoto';
import { PropertyPhotoImage } from 'components/marketplace/shared/PropertyPhoto';
import { useImagePinchZoom } from 'components/marketplace/shared/useImagePinchZoom';
import { useTouring } from 'components/self-tour/hooks/useTouring';
import { formatCurrency } from 'helpers/formatters';
import { createPortal } from 'react-dom';

import { BuyDirectContextModal } from './PropertySection/BuyDirectContextModal';
import { SelfTourDialog } from './PropertySection/SelfTourDialog';
import { TourWithAgentModal } from './PropertySection/TourWithAgentModal';

type LightboxProperty = Parameters<typeof useTouring>[0] &
  Pick<
    PropertyDetailsType,
    | 'city'
    | 'floorPlanPhotos'
    | 'tourBookingListingId'
    | 'isFavoriteLoading'
    | 'isLiked'
    | 'listPrice'
    | 'listingHistory'
    | 'onToggleFavorite'
    | 'photos'
    | 'photosLg'
    | 'photosSm'
    | 'photosXl'
    | 'photosXs'
    | 'slug'
    | 'state'
    | 'street'
    | 'unitNumber'
    | 'unitType'
    | 'zip'
  >;

interface LightboxProps {
  handleClose: () => void;
  isOpen: boolean;
  property: LightboxProperty;
  selectedPhotoIndex: number;
}

/** Primary photo with two-finger pinch-to-zoom (mobile). */
const ZoomableImage = ({
  photo,
  alt,
  lazy,
}: {
  photo: GalleryPhoto;
  alt: string;
  lazy: boolean;
}) => {
  const imageRef = useImagePinchZoom(true);
  return (
    <GalleryListingPhoto
      imageRef={imageRef}
      photo={photo}
      sizes={LIGHTBOX_BREAKPOINT_TIERS}
      lazy={lazy}
      fetchPriority={lazy ? undefined : 'high'}
      alt={alt}
      objectFit="contain"
      fit="contain"
      className="h-full w-full"
    />
  );
};

export const Lightbox = ({ handleClose, isOpen, property, selectedPhotoIndex }: LightboxProps) => {
  const formattedStreet = formatStreetAddress(
    property.street,
    property.unitType,
    property.unitNumber,
  );
  const galleryPhotos = useMemo(() => buildGalleryPhotos(property), [property]);
  const thumbnailUrls = useMemo(
    () => galleryPhotos.map((photo) => getLightboxThumbnailUrl(photo)),
    [galleryPhotos],
  );
  const count = galleryPhotos.length;

  const [index, setIndex] = useState(selectedPhotoIndex || 0);
  const [isDesktop] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(min-width: 768px)').matches,
  );
  const activeThumbRef = useRef<HTMLElement>(null);
  const skipThumbScrollAnimationRef = useRef(true);

  useEffect(() => {
    if (isOpen) {
      // oxlint-disable-next-line react/set-state-in-effect -- react19-baseline: pre-existing, do not copy. Derive during render or set from the event instead
      setIndex(selectedPhotoIndex || 0);
      skipThumbScrollAnimationRef.current = true;
    }
  }, [isOpen, selectedPhotoIndex]);

  // Body scroll lock + global keyboard nav while open.
  useEffect(() => {
    if (!isOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') handleClose();
      else if (event.key === 'ArrowRight') setIndex((i) => (i + 1) % count);
      else if (event.key === 'ArrowLeft') setIndex((i) => (i - 1 + count) % count);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen, handleClose, count]);

  // Keep the active thumbnail scrolled into view.
  useEffect(() => {
    if (!activeThumbRef.current) {
      return;
    }
    activeThumbRef.current.scrollIntoView({
      behavior: skipThumbScrollAnimationRef.current ? 'auto' : 'smooth',
      block: 'nearest',
      inline: 'nearest',
    });
    skipThumbScrollAnimationRef.current = false;
    // oxlint-disable-next-line react/exhaustive-effect-dependencies -- react19-baseline: pre-existing, do not copy. Fix the dependency array rather than suppressing
  }, [index]);

  const displayPriceDollars = getDisplayPrice(property, 'purchase');
  let displayPrice = 'Price not available';
  if (displayPriceDollars) {
    displayPrice = formatCurrency(displayPriceDollars, { inDollars: true });
  } else if (property.listingState === 'AVAILABLE_SOON') {
    displayPrice = 'Price pending';
  }

  if (!isOpen) return null;

  const mobileTitle = (
    <Col className="flex-1 items-center md:hidden">
      <Text as="span" className="text-label-md text-od-content-inverse-primary">
        {displayPrice}
      </Text>
      <Text as="span" className="text-body-sm text-od-content-inverse-primary">
        {formattedStreet}
      </Text>
    </Col>
  );

  return createPortal(
    <Col
      role="dialog"
      aria-modal="true"
      aria-label={`Photo lightbox, image ${index + 1} of ${count}`}
      className="fixed inset-0 z-[3000] bg-black"
    >
      {/* Header */}
      <Row className="w-full items-center justify-between gap-4 px-6 pb-2 pt-4 md:px-12">
        <Box className="w-16">
          <Button
            variant="secondaryInverse"
            aria-label="back"
            analyticsName="cosmos-marketplace-property-photo-lightbox-mobile-close"
            onClick={handleClose}
            className="size-7 rounded-full bg-transparent p-0 md:hidden"
          >
            <IconChevronLeft size={20} />
          </Button>
          <Text as="span" className="hidden text-body-md text-od-content-inverse-primary md:block">
            {index + 1} of {count}
          </Text>
        </Box>
        {mobileTitle}
        <Box className="w-16 items-end">
          <Button
            variant="secondaryInverse"
            aria-label="close"
            analyticsName="cosmos-marketplace-property-photo-lightbox-desktop-close"
            onClick={handleClose}
            className="hidden size-7 rounded-full bg-transparent p-0 md:flex"
          >
            <IconX size={20} />
          </Button>
          <Text as="span" className="text-body-md text-od-content-inverse-primary md:hidden">
            {index + 1} of {count}
          </Text>
        </Box>
      </Row>

      {/* Swipeable photo area */}
      <Carousel
        count={count}
        index={index}
        onIndexChange={setIndex}
        wrap
        disableKeyboard
        ariaLabel={`Image carousel, showing image ${index + 1} of ${count}`}
        className="flex-1"
        slideClassName="items-center justify-center"
        renderSlide={(i) => {
          const photo = galleryPhotos[i];
          if (!photo) {
            return null;
          }
          return (
            <ZoomableImage
              photo={photo}
              alt={
                photo.isFloorPlan
                  ? photo.label?.trim() || 'Floor plan'
                  : `Property photo ${i + 1} of ${count}`
              }
              lazy={i !== index}
            />
          );
        }}
      >
        <Button
          variant="secondaryInverse"
          aria-label="back"
          analyticsName="cosmos-marketplace-property-photo-lightbox-back-button"
          onClick={() => setIndex((i) => (i - 1 + count) % count)}
          className="absolute left-6 top-[40%] z-[1] hidden size-9 rounded-full bg-transparent p-0 md:flex"
        >
          <IconChevronLeft size={24} />
        </Button>
        <Button
          variant="secondaryInverse"
          aria-label="forward"
          analyticsName="cosmos-marketplace-property-photo-lightbox-forward-button"
          onClick={() => setIndex((i) => (i + 1) % count)}
          className="absolute right-6 top-[40%] z-[1] hidden size-9 rounded-full bg-transparent p-0 md:flex"
        >
          <IconChevronRight size={24} />
        </Button>
      </Carousel>

      {/* Desktop thumbnail strip */}
      {isDesktop && (
        <Row className="w-full gap-2 overflow-x-auto px-6 py-4">
          {thumbnailUrls.map((thumbnail, i) => (
            <Box
              key={i}
              ref={i === index ? activeThumbRef : undefined}
              onClick={() => setIndex(i)}
              aria-label={`Lightbox carousel photo ${i + 1}`}
              analyticsName={`lightbox-photo-item-${i}`}
              className={cn(
                'h-18 w-24 shrink-0 overflow-hidden border-4 p-0',
                i === index ? 'border-od-border-tertiary' : 'border-transparent',
              )}
            >
              <PropertyPhotoImage
                src={thumbnail}
                alt={`Carousel photo ${i + 1}`}
                loading="lazy"
                containerClassName="h-full w-full"
                className={cn(
                  'h-full w-full',
                  galleryPhotos[i]?.isFloorPlan ? 'object-contain' : 'object-cover',
                )}
              />
            </Box>
          ))}
        </Row>
      )}

      {/* Desktop footer */}
      <Row className="w-full justify-between px-6 pb-8 pt-2 max-md:hidden">
        <Row className="flex-1 items-center">
          <Text as="span" className="text-label-md text-od-content-inverse-primary">
            {displayPrice}
          </Text>
          <Text as="span" className="pl-1 text-body-md text-od-content-inverse-primary">
            — {formattedStreet}, {property.city}, {property.state} {property.zip}
          </Text>
        </Row>
        <Row className="items-center justify-end gap-6 self-end">
          <Button
            variant="secondaryInverse"
            aria-label={property.isLiked ? 'unfavorite' : 'favorite'}
            analyticsName="cosmos-marketplace-pdp-lightbox-favorite-property-button"
            onClick={property.onToggleFavorite}
            loading={property.isFavoriteLoading}
            className="size-7 rounded-full bg-transparent p-0"
          >
            {property.isLiked ? (
              <IconHeartFilled size={20} className="text-od-content-error" />
            ) : (
              <IconHeart size={20} />
            )}
          </Button>
          <TourButton property={property} />
        </Row>
      </Row>
    </Col>,
    document.body,
  );
};

type TourAction = NonNullable<ReturnType<typeof useTouring>>['primaryAction'];

// atoms Button splits link vs button by `href`, so an action with an href (and
// not disabled) renders as an anchor; everything else renders as a button.
const renderTourAction = (action: TourAction) => (
  <Button
    analyticsName={action.analyticsName}
    data-analytics-name={action.analyticsName}
    aria-label={action.text}
    variant={action.variant}
    size="lg"
    onClick={action.onClick}
    disabled={action.disabled}
    href={action.disabled ? undefined : action.href}
    target={action.target}
    rel="noopener"
  >
    {action.text}
  </Button>
);

interface TourButtonProps {
  property: LightboxProperty;
}
const TourButton: React.FC<TourButtonProps> = ({ property }) => {
  const touringInfo = useTouring(property, { placement: 'lightbox' });
  if (!touringInfo) return null;

  const {
    primaryAction,
    secondaryAction,
    tourKind,
    closeDialog,
    isDialogOpen,
    isSelfTourDialogOpen,
    openSelfTourDialog,
    closeSelfTourDialog,
    isBuyDirectContextOpen,
    closeBuyDirectContext,
    buyDirectContextHref,
  } = touringInfo;

  return (
    <>
      <Row className="items-center gap-1">
        {secondaryAction && renderTourAction(secondaryAction)}
        {renderTourAction(primaryAction)}
      </Row>
      {tourKind === 'tourWithAgent' && (
        <TourWithAgentModal property={property} isOpen={isDialogOpen} onClose={closeDialog} />
      )}
      <SelfTourDialog
        isOpen={isSelfTourDialogOpen}
        onClose={closeSelfTourDialog}
        onOpenRequest={openSelfTourDialog}
        placement="lightbox"
        addressToken={property.addressToken}
        slug={property.slug}
        tourBookingListingId={property.tourBookingListingId}
        addressLine={[property.street, property.city].filter(Boolean).join(', ')}
      />
      <BuyDirectContextModal
        isOpen={isBuyDirectContextOpen}
        onClose={closeBuyDirectContext}
        continueHref={buyDirectContextHref}
      />
    </>
  );
};
